<template>
  <div class="min-h-screen bg-gradient-to-b from-[#142a4a] via-[#18365f] to-[#1b4475]">
    <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-4 pb-8">
      <div
        class="flex flex-col sm:flex-row sm:justify-between gap-4"
        :class="listViewMode === 'network' ? 'sm:items-end mb-6' : 'sm:items-center mb-8'"
      >
        <div>
          <template v-if="listViewMode === 'table'">
            <h1 class="text-3xl font-bold text-slate-100">{{ tableHeadingTitle }}</h1>
            <p class="text-slate-400 mt-1 text-sm">
              {{ tableHeadingSubtitle }}
            </p>
          </template>
          <template v-else>
            <h1 class="text-3xl font-bold text-slate-100">Redes de Agentes</h1>
            <p class="text-slate-400 mt-1 text-sm max-w-2xl">
              Coordenadores à esquerda, executores à direita;
              Clique num <strong class="text-cyan-300/90 font-medium">coordenador</strong> ou num
              <strong class="text-cyan-300/90 font-medium">executor</strong>
            </p>
          </template>
        </div>
        <div class="flex flex-wrap items-center gap-2 shrink-0">
          <button
            v-if="listViewMode === 'table' && tenantAdmin"
            type="button"
            class="inline-flex items-center gap-2 px-4 py-2 rounded-lg border border-cyan-400/45 bg-slate-900/60 text-cyan-200 text-sm font-medium hover:bg-cyan-400/10 hover:border-cyan-400/70 transition"
            @click="openAgentsNetworkView"
          >
            <svg class="w-4 h-4 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="1.8"
                d="M6 18a3 3 0 100-6 3 3 0 000 6zm12-8a3 3 0 100-6 3 3 0 000 6zm-6 4a3 3 0 100-6 3 3 0 000 6zm6 4a3 3 0 100-6 3 3 0 000 6M8.59 13.51l6.83 3.98m.01-7.98l-6.86 3.99"
              />
            </svg>
            Modo Rede
          </button>
          <template v-else>
            <button
              type="button"
              class="inline-flex items-center justify-center gap-2 px-4 py-2 rounded-lg border border-cyan-400/45 bg-slate-900/60 text-cyan-200 text-sm font-medium hover:bg-cyan-400/10 hover:border-cyan-400/70 transition shrink-0 disabled:opacity-50 disabled:pointer-events-none"
              :disabled="agentsNetworkLoading"
              @click="refreshAgentsNetwork"
            >
              <svg class="w-4 h-4 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                />
              </svg>
              Atualizar
            </button>
            <button
              v-if="tenantAdmin"
              type="button"
              class="inline-flex items-center gap-2 px-4 py-2 rounded-lg border border-slate-500/60 bg-slate-900/60 text-slate-200 text-sm font-medium hover:bg-slate-800/80 hover:border-slate-400/70 transition"
              @click="closeAgentsNetworkView"
            >
              <svg class="w-4 h-4 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="1.8"
                  d="M3 10h18M3 14h18m-9-4v8m-7 0h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z"
                />
              </svg>
              Modo Card
            </button>
          </template>
        <div v-if="tenantAdmin" class="relative">
          <button
            v-if="agentTypeFilterLocked && fixedAgentType === 'coordinator'"
            type="button"
            class="inline-flex items-center justify-center gap-2 px-4 py-2 rounded-lg bg-[#2AD7DE] hover:bg-[#5de9f0] text-slate-950 text-sm font-semibold transition"
            @click="openCreate('coordinator')"
          >
            Novo coordenador
          </button>
          <button
            v-else-if="agentTypeFilterLocked && fixedAgentType === 'executor'"
            type="button"
            class="inline-flex items-center justify-center gap-2 px-4 py-2 rounded-lg bg-[#2AD7DE] hover:bg-[#5de9f0] text-slate-950 text-sm font-semibold transition"
            @click="openCreate('executor')"
          >
            Novo executor
          </button>
          <template v-else>
            <button
              type="button"
              class="inline-flex items-center justify-center gap-2 px-4 py-2 rounded-lg bg-[#2AD7DE] hover:bg-[#5de9f0] text-slate-950 text-sm font-semibold transition"
              @click="toggleCreateMenu"
            >
              Novo agente
              <svg class="w-4 h-4" viewBox="0 0 20 20" fill="currentColor">
                <path fill-rule="evenodd" d="M5.23 7.21a.75.75 0 011.06.02L10 11.172l3.71-3.94a.75.75 0 111.08 1.04l-4.25 4.51a.75.75 0 01-1.08 0l-4.25-4.51a.75.75 0 01.02-1.06z" clip-rule="evenodd" />
              </svg>
            </button>
            <div
              v-if="createMenuOpen"
              class="absolute right-0 mt-2 min-w-[12rem] rounded-lg border border-cyan-400/35 bg-slate-900 shadow-lg shadow-black/40 z-20 overflow-hidden"
            >
              <button
                type="button"
                class="w-full text-left px-3 py-2 text-sm text-slate-100 hover:bg-cyan-400/15 inline-flex items-center gap-2"
                @click="openCreate('coordinator')"
              >
                <svg class="w-4 h-4 text-cyan-300 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.8" d="M15 17h5l-1.405-1.405M20 17l-1.405 1.405M4 5h10a2 2 0 012 2v4a2 2 0 01-2 2H8l-4 4V7a2 2 0 012-2z" />
                </svg>
                Coordenador
              </button>
              <button
                type="button"
                class="w-full text-left px-3 py-2 text-sm text-slate-100 hover:bg-cyan-400/15 inline-flex items-center gap-2"
                @click="openCreate('executor')"
              >
                <svg class="w-4 h-4 text-cyan-300 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.8" d="M12 6v6l4 2M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                Executor
              </button>
            </div>
          </template>
        </div>
        </div>
      </div>

      <template v-if="listViewMode === 'table'">
      <div class="mb-4 flex flex-col gap-3 lg:flex-row lg:items-end lg:gap-3">
        <div class="flex min-w-0 w-full flex-1 flex-col gap-3 sm:flex-row sm:items-stretch sm:gap-3">
          <input
            v-model="searchQ"
            type="search"
            placeholder="Buscar por nome ou descrição…"
            class="w-full min-w-0 flex-1 px-4 py-2 rounded-lg border border-slate-600 bg-slate-900/70 text-sm text-slate-100 placeholder:text-slate-300 focus:outline-none focus:ring-2 focus:ring-cyan-400/50 focus:ring-offset-0 focus:border-cyan-400/70"
            @input="debouncedLoad"
          />
          <select
            v-if="!agentTypeFilterLocked"
            v-model="filterAgentType"
            class="w-full min-w-0 flex-1 px-4 py-2 rounded-lg border border-slate-600 bg-slate-900/70 text-sm focus:outline-none focus:ring-2 focus:ring-cyan-400/50 focus:border-cyan-400/70"
            :class="filterAgentType ? 'text-slate-100' : 'text-slate-300'"
            @change="loadAgents(1)"
          >
            <option value="">Tipo: todos</option>
            <option value="coordinator">Coordenador</option>
            <option value="executor">Executor</option>
          </select>
          <select
            v-if="fixedAgentType !== 'coordinator'"
            v-model="filterSpecialty"
            class="w-full min-w-0 flex-1 px-4 py-2 rounded-lg border border-slate-600 bg-slate-900/70 text-sm focus:outline-none focus:ring-2 focus:ring-cyan-400/50 focus:border-cyan-400/70"
            :class="filterSpecialty ? 'text-slate-100' : 'text-slate-300'"
            @change="loadAgents(1)"
          >
            <option value="">Especialidade: todas</option>
            <option value="data_analyst">Analista de dados</option>
            <option value="record_creation">Criação de Cadastros</option>
            <option value="knowledge_specialist">Especialista em conhecimento</option>
            <option value="email_analyst">Analista de e-mail</option>
          </select>
        </div>
        <button
          type="button"
          class="inline-flex items-center justify-center gap-2 px-4 py-2 rounded-lg border border-cyan-400/45 bg-slate-900/60 text-cyan-200 text-sm font-medium hover:bg-cyan-400/10 hover:border-cyan-400/70 transition self-end lg:self-auto shrink-0 disabled:opacity-50 disabled:pointer-events-none"
          :disabled="loading"
          @click="refreshAgentsList"
        >
          <svg class="w-4 h-4 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
            />
          </svg>
          Atualizar
        </button>
      </div>

      <div v-if="loadError" class="mb-4 p-4 bg-red-500/10 border border-red-400/40 text-red-300 rounded-lg text-sm">
        {{ loadError }}
      </div>

      <div v-if="loading" class="text-center py-12 text-slate-400">Carregando…</div>

      <ul v-else class="space-y-3">
        <li
          v-for="a in agents"
          :key="a.id"
          class="rounded-xl border-2 border-cyan-400/35 bg-slate-900/85 backdrop-blur-sm shadow-xl shadow-black/30 p-4 flex flex-col sm:flex-row sm:items-center gap-4 hover:border-cyan-400/70 hover:bg-slate-900/90 transition"
        >
          <div class="flex items-center gap-3 flex-1 min-w-0">
            <AgentAvatar
              :variant="a.avatar_variant"
              :color="a.avatar_color"
              size="dashboard"
              eyes-pulse
              :name="a.name"
            />
            <div class="min-w-0">
              <div class="flex items-center gap-2 flex-wrap">
                <h2 class="font-semibold text-slate-100 truncate">{{ a.name }}</h2>
                <span
                  class="inline-flex items-center gap-2 text-xs px-2 py-0.5 rounded-full"
                  :class="a.active ? 'bg-emerald-500/15 text-emerald-300 border border-emerald-400/30' : 'bg-slate-800 text-slate-400 border border-slate-600'"
                >
                  <template v-if="a.active">
                    <span class="relative inline-flex h-2 w-2">
                      <span class="absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-80 animate-ping" />
                      <span class="relative inline-flex h-2 w-2 rounded-full bg-emerald-400" />
                    </span>
                    <span>Ativo</span>
                  </template>
                  <template v-else>Inativo</template>
                </span>
                <span class="text-xs px-2 py-0.5 rounded-full border border-cyan-400/35 bg-cyan-400/10 text-cyan-200">
                  <span class="inline-flex items-center gap-1">
                    <svg
                      v-if="a.agent_type === 'coordinator'"
                      class="w-3.5 h-3.5"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.8" d="M15 17h5l-1.405-1.405M20 17l-1.405 1.405M4 5h10a2 2 0 012 2v4a2 2 0 01-2 2H8l-4 4V7a2 2 0 012-2z" />
                    </svg>
                    <svg
                      v-else
                      class="w-3.5 h-3.5"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.8" d="M12 6v6l4 2M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <span>{{ a.agent_type === 'coordinator' ? 'Coordenador' : 'Executor' }}</span>
                  </span>
                </span>
              </div>
              <template v-if="a.agent_type === 'executor'">
                <p class="text-sm text-slate-400">{{ formatExecutorSpecialty(a) }}</p>
                <p
                  v-if="formatDescriptionSnippet(a)"
                  class="text-sm text-slate-400/70 mt-0.5 line-clamp-3 break-words"
                  :title="agentDescriptionTitle(a)"
                >
                  {{ formatDescriptionSnippet(a) }}
                </p>
              </template>
              <p
                v-else
                class="text-sm text-slate-400 line-clamp-3 break-words"
                :title="agentDescriptionTitle(a)"
              >
                {{ formatCoordinatorCardSecondary(a) }}
              </p>
            </div>
          </div>
          <div class="flex flex-wrap gap-2 sm:justify-end">
            <button
              type="button"
              class="px-3 py-1.5 text-sm rounded-lg border border-slate-500/60 bg-slate-900/30 text-slate-200 hover:bg-slate-800/80 hover:border-slate-400/70 transition"
              @click="demoRun(a)"
              :disabled="demoLoadingId === a.id"
            >
              {{ demoLoadingId === a.id ? 'Demo…' : 'Rodar demo' }}
            </button>
            <button
              type="button"
              class="px-3 py-1.5 text-sm rounded-lg border border-cyan-400/45 bg-cyan-400/5 text-cyan-200 hover:bg-cyan-400/12 hover:border-cyan-400/70 transition"
              @click="openEdit(a)"
            >
              Editar
            </button>
            <button
              type="button"
              class="px-3 py-1.5 text-sm rounded-lg border border-red-400/45 bg-red-500/5 text-red-200 hover:bg-red-500/20 hover:border-red-400/70 transition"
              @click="confirmDestroy(a)"
            >
              Excluir
            </button>
          </div>
        </li>
      </ul>

      <div v-if="!loading && agents.length === 0" class="text-center py-12 text-slate-400">
        Nenhum agente ainda. Crie o primeiro.
      </div>

      <div v-if="pagy && pagy.pages > 1" class="mt-6 flex justify-center gap-2 flex-wrap">
        <button
          v-for="p in pageButtons"
          :key="p"
          type="button"
          class="px-3 py-1 rounded border text-sm"
          :class="p === pagy.page ? 'border-cyan-400/60 bg-cyan-400/15 text-cyan-300' : 'border-slate-600 text-slate-300 hover:bg-slate-800/70'"
          @click="goPage(p)"
        >
          {{ p }}
        </button>
      </div>
      </template>

      <AgentNetwork
        v-else
        ref="agentsNetworkEmbed"
        class="pb-4"
        @loading-state="agentsNetworkLoading = $event"
        @edit-selected="onNetworkNodeSelected"
      />

    </div>

    <!-- Modal (sem fechar ao clicar fora — alinhado ao cadastro de usuários / CrudBase) -->
    <div
      v-if="modalOpen"
      class="fixed inset-0 z-[80] flex items-center justify-center p-4 bg-slate-950/50 backdrop-blur-sm"
    >
      <div class="bg-[#435b7c]/95 border border-cyan-400/25 rounded-xl shadow-2xl shadow-black/45 max-w-3xl w-full max-h-[90vh] flex flex-col overflow-hidden">
        <div class="px-6 py-4 bg-[#2f4563] border-b border-cyan-200/25 flex justify-between items-start gap-3 flex-shrink-0">
          <h3 class="text-lg font-semibold text-slate-100">{{ editingId ? 'Editar agente' : 'Novo agente' }}</h3>
          <button
            type="button"
            class="text-slate-500 hover:text-cyan-300 p-1 -mr-1 transition"
            aria-label="Fechar"
            @click="closeAgentModal"
          >
            <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
        <form class="flex flex-col flex-1 min-h-0" @submit.prevent="openSaveConfirm">
          <div
            class="px-6 py-4 overflow-y-auto flex-1 [scrollbar-width:thin] [scrollbar-color:rgba(103,232,249,0.45)_rgba(47,69,99,0.4)] [&::-webkit-scrollbar]:w-2 [&::-webkit-scrollbar-track]:rounded-full [&::-webkit-scrollbar-track]:bg-[#2f4563]/40 [&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-thumb]:bg-cyan-300/45 [&::-webkit-scrollbar-thumb]:border [&::-webkit-scrollbar-thumb]:border-[#2f4563]/80 [&::-webkit-scrollbar-thumb:hover]:bg-cyan-300/70"
          >
            <div v-if="editModalLoading" class="py-16 text-center text-slate-400 text-sm">
              Carregando dados do agente…
            </div>
            <div v-else class="space-y-4">
              <AgentAvatarPicker
                :key="`agent-avatar-${editingId || 'new'}`"
                :variant="form.avatar_variant"
                :color="form.avatar_color"
                :preview-name="form.name || 'Agente'"
                :agent-type="form.agent_type"
                @update:variant="onAvatarVariant"
                @update:color="onAvatarColor"
              />
              <div class="-mt-2 flex justify-center">
                <span class="inline-flex items-center gap-1 px-2 py-0.5 rounded border border-cyan-400/35 bg-cyan-400/10 text-cyan-200 text-xs">
                  <svg
                    v-if="form.agent_type === 'coordinator'"
                    class="w-3.5 h-3.5"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.8" d="M15 17h5l-1.405-1.405M20 17l-1.405 1.405M4 5h10a2 2 0 012 2v4a2 2 0 01-2 2H8l-4 4V7a2 2 0 012-2z" />
                  </svg>
                  <svg
                    v-else
                    class="w-3.5 h-3.5"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.8" d="M12 6v6l4 2M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <span>{{ form.agent_type === 'coordinator' ? 'Coordenador' : 'Executor' }}</span>
                </span>
              </div>

              <div class="grid grid-cols-1 sm:grid-cols-12 gap-3">
                <div :class="form.agent_type === 'executor' ? 'sm:col-span-7' : 'sm:col-span-8'">
                  <label class="block text-sm font-medium text-slate-300 mb-1">Nome <span class="text-red-400">*</span></label>
                  <input v-model="form.name" required class="w-full px-3 py-2 rounded-lg border border-slate-500/80 bg-slate-800/55 text-slate-100 focus:outline-none focus:ring-2 focus:ring-cyan-400/40 focus:border-cyan-400/70" />
                </div>
                <div v-if="form.agent_type === 'executor'" class="sm:col-span-5">
                  <label class="block text-sm font-medium text-slate-300 mb-1">Especialidade</label>
                  <select
                    v-model="form.specialty"
                    class="w-full px-3 py-2 rounded-lg border border-slate-500/80 bg-slate-800/55 text-slate-100 focus:outline-none focus:ring-2 focus:ring-cyan-400/40 focus:border-cyan-400/70"
                  >
                    <option value="data_analyst">Analista de dados</option>
                    <option value="record_creation">Criação de Cadastros</option>
                    <option value="knowledge_specialist">Especialista em conhecimento</option>
                    <option value="email_analyst">Analista de e-mail</option>
                  </select>
                </div>
                <div v-else class="sm:col-span-4 flex flex-col">
                  <label
                    class="mb-1 hidden select-none text-sm font-medium text-slate-300 sm:block sm:invisible sm:pointer-events-none"
                    aria-hidden="true"
                  >
                    Nome <span class="text-red-400">*</span>
                  </label>
                  <div
                    class="flex min-h-[42px] w-full items-center gap-2.5 rounded-lg border border-slate-500/80 bg-slate-800/55 px-3 py-2 box-border"
                  >
                    <input
                      id="agent-responds-chat"
                      v-model="form.responds_to_chat"
                      type="checkbox"
                      class="shrink-0 rounded border-slate-600 bg-slate-900 text-cyan-400 focus:ring-cyan-400/50"
                    />
                    <label for="agent-responds-chat" class="cursor-pointer text-sm leading-snug text-slate-300">
                      Disponível no chat
                    </label>
                  </div>
                </div>
              </div>
              <div>
                <label class="block text-sm font-medium text-slate-300 mb-1">Descrição</label>
                <textarea v-model="form.description" rows="2" class="w-full px-3 py-2 rounded-lg border border-slate-500/80 bg-slate-800/55 text-slate-100 focus:outline-none focus:ring-2 focus:ring-cyan-400/40 focus:border-cyan-400/70" />
              </div>
              <div>
                <label class="block text-sm font-medium text-slate-300 mb-1">Prompt (Instruções para o Agente)</label>
                <textarea v-model="form.system_prompt" rows="10" class="w-full px-3 py-2 rounded-lg border border-slate-500/80 bg-slate-800/55 text-slate-100 focus:outline-none focus:ring-2 focus:ring-cyan-400/40 focus:border-cyan-400/70 font-mono text-sm" />
              </div>
              <div v-if="form.agent_type === 'executor'">
                <label class="block text-sm font-medium text-slate-300 mb-2">Configuração da especialidade</label>

                <AgentSpecialtyDataAnalystForm
                  v-if="form.specialty === 'data_analyst'"
                  :key="`da-form-${dataAnalystFormKey}`"
                  ref="dataAnalystSpecialtyForm"
                  :form="form"
                  :filtered-api-endpoint-options="filteredApiEndpointOptions"
                  :database-connector-view-options="connectorOptions.databaseConnectorViewOptions"
                  :whatsapp-delivery-connector-options="connectorOptions.whatsappDeliveryConnectorOptions"
                  :selected-api-endpoint-to-add="selectedApiEndpointToAdd"
                  :selected-database-connector-view-to-add="selectedDatabaseConnectorViewToAdd"
                  :week-day-options="weekDayOptions"
                  @update:selectedApiEndpointToAdd="selectedApiEndpointToAdd = $event"
                  @update:selectedDatabaseConnectorViewToAdd="selectedDatabaseConnectorViewToAdd = $event"
                  @add-api-endpoint="addSelectedApiEndpoint"
                  @remove-api-endpoint="removeApiEndpoint"
                  @add-database-view="addSelectedDatabaseConnectorView"
                  @remove-database-view="removeDatabaseConnectorView"
                />

                <AgentSpecialtyRecordCreationForm
                  v-else-if="form.specialty === 'record_creation'"
                  :form="form"
                  :filtered-api-endpoint-options="filteredApiEndpointOptions"
                  :selected-api-endpoint-to-add="selectedApiEndpointToAdd"
                  @update:selectedApiEndpointToAdd="selectedApiEndpointToAdd = $event"
                  @add-api-endpoint="addSelectedApiEndpoint"
                  @remove-api-endpoint="removeApiEndpoint"
                />

                <AgentSpecialtyKnowledgeForm
                  v-else-if="form.specialty === 'knowledge_specialist'"
                  :form="form"
                  :filtered-api-endpoint-options="filteredApiEndpointOptions"
                  :selected-api-endpoint-to-add="selectedApiEndpointToAdd"
                  :editing-id="editingId"
                  :knowledge-reindexing="knowledgeReindexing"
                  :knowledge-files="knowledgeFiles"
                  :knowledge-documents="knowledgeDocuments"
                  :knowledge-uploading="knowledgeUploading"
                  :knowledge-file-input-key="knowledgeFileInputKey"
                  :last-reindex-run="lastKnowledgeReindexRun"
                  :urls="form.settings.knowledge.urls"
                  @update:selectedApiEndpointToAdd="selectedApiEndpointToAdd = $event"
                  @add-api-endpoint="addSelectedApiEndpoint"
                  @remove-api-endpoint="removeApiEndpoint"
                  @reindex="reindexKnowledge"
                  @files-selected="onKnowledgeFilesSelected"
                  @remove-document="removeKnowledgeDocument"
                  @add-url="addKnowledgeUrlRow"
                  @remove-url="removeKnowledgeUrlRow"
                  @update-url="updateKnowledgeUrl"
                />

                <AgentSpecialtyEmailAnalystForm
                  v-else-if="form.specialty === 'email_analyst'"
                  :form="form"
                  :options="connectorOptions.emailInboxConnectorOptions"
                  :whatsapp-delivery-connector-options="connectorOptions.whatsappDeliveryConnectorOptions"
                  :week-day-options="weekDayOptions"
                  :selected-id="selectedEmailInboxConnectorToAdd"
                  @update:selectedId="selectedEmailInboxConnectorToAdd = $event"
                  @add="addSelectedEmailInboxConnector"
                  @remove="removeEmailInboxConnector"
                />
              </div>

              <AgentCoordinatorConfigForm
                v-if="form.agent_type === 'coordinator'"
                :key="coordinatorFormMountKey"
                :form="form"
                :executor-summaries="allowedExecutorSummaries"
                :editing-id="editingId"
                @add-executor="addSelectedExecutor"
                @remove-executor="removeExecutor"
              />

              <div class="flex items-center gap-2">
                <input id="agent-active" v-model="form.active" type="checkbox" class="rounded border-slate-600 bg-slate-900 text-cyan-400 focus:ring-cyan-400/50" />
                <label for="agent-active" class="text-sm text-slate-300">Agente ativo</label>
              </div>
            </div>
          </div>
          <div class="px-6 py-4 border-t border-slate-600/70 flex flex-col gap-3 flex-shrink-0 bg-slate-700/55">
            <div
              v-if="formErrors.length && !editModalLoading"
              role="alert"
              class="relative w-full rounded-lg border border-red-400/50 bg-red-500/15 pl-3 pr-10 py-2.5 text-sm text-red-100 leading-snug"
            >
              <button
                type="button"
                class="absolute top-2 right-2 p-1 rounded-md text-red-200/90 hover:text-red-50 hover:bg-red-500/25 transition leading-none"
                aria-label="Fechar aviso"
                @click="dismissAgentFormErrors"
              >
                <span class="text-lg font-light" aria-hidden="true">×</span>
              </button>
              <span class="font-semibold text-red-50 block mb-1 pr-1">Não foi possível salvar.</span>
              <span class="text-red-100/95 break-words pr-1">{{ formErrors.join(' · ') }}</span>
            </div>
            <div class="flex justify-end gap-2">
              <button
                type="submit"
                class="px-4 py-2 text-sm rounded-lg bg-[#2AD7DE] hover:bg-[#5de9f0] text-slate-950 font-semibold transition"
                :disabled="saving || editModalLoading"
              >
                {{ saving ? 'Salvando…' : 'Salvar' }}
              </button>
              <button type="button" class="px-4 py-2 text-sm rounded-lg border border-slate-600 text-slate-300 hover:bg-slate-800/70" @click="closeAgentModal">
                Sair
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>

    <!-- Confirmação excluir (mesmo padrão CrudBase / usuários) -->
    <div
      v-if="deleteConfirmAgent"
      class="fixed inset-0 bg-slate-950/50 backdrop-blur-sm flex items-center justify-center z-[90] p-4"
      @click.self="closeDeleteModal"
    >
      <div class="bg-[#435b7c]/95 border border-cyan-400/25 rounded-xl shadow-2xl shadow-black/45 max-w-2xl p-6 max-h-[90vh] overflow-y-auto [scrollbar-width:thin] [scrollbar-color:rgba(103,232,249,0.45)_rgba(47,69,99,0.4)] [&::-webkit-scrollbar]:w-2 [&::-webkit-scrollbar-track]:rounded-full [&::-webkit-scrollbar-track]:bg-[#2f4563]/40 [&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-thumb]:bg-cyan-300/45 [&::-webkit-scrollbar-thumb]:border [&::-webkit-scrollbar-thumb]:border-[#2f4563]/80 [&::-webkit-scrollbar-thumb:hover]:bg-cyan-300/70">
        <h3 class="flex items-center text-lg font-semibold text-slate-100 mb-4">
          <svg class="w-6 h-6 text-amber-500 mr-2 shrink-0" fill="currentColor" viewBox="0 0 20 20">
            <path
              fill-rule="evenodd"
              d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l6.518 11.59C19.02 16.92 18.145 18 16.757 18H3.243c-1.388 0-2.263-1.08-1.504-2.31l6.518-11.59zM11 14a1 1 0 10-2 0 1 1 0 002 0zm-1-8a1 1 0 00-.894.553L9 7v4a1 1 0 002 0V7l-.106-.447A1 1 0 0010 6z"
              clip-rule="evenodd"
            />
          </svg>
          Confirmação!
        </h3>
        <p class="text-slate-300 mb-6">
          Tem certeza que deseja excluir o agente <strong class="text-slate-100">{{ deleteConfirmAgent.name }}</strong>?<br />
          Esta ação não pode ser desfeita.
        </p>
        <div class="flex justify-end space-x-3">
          <button
            type="button"
            :disabled="deleting"
            class="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-500 transition disabled:opacity-50"
            @click="executeDestroy"
          >
            {{ deleting ? 'Excluindo…' : 'Sim' }}
          </button>
          <button
            type="button"
            class="px-4 py-2 border border-slate-600 rounded-lg text-slate-300 hover:bg-slate-800/70 transition"
            @click="closeDeleteModal"
          >
            Não
          </button>
        </div>
      </div>
    </div>

    <!-- Confirmação salvar -->
    <div
      v-if="saveConfirmOpen"
      class="fixed inset-0 bg-slate-950/50 backdrop-blur-sm flex items-center justify-center z-[90] p-4"
      @click.self="closeSaveConfirm"
    >
      <div class="bg-[#435b7c]/95 border border-cyan-400/25 rounded-xl shadow-2xl shadow-black/45 max-w-md w-full p-6">
        <h3 class="flex items-center text-lg font-semibold text-slate-100 mb-4">
          <svg class="w-6 h-6 text-amber-500 mr-2 shrink-0" fill="currentColor" viewBox="0 0 20 20">
            <path
              fill-rule="evenodd"
              d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l6.518 11.59C19.02 16.92 18.145 18 16.757 18H3.243c-1.388 0-2.263-1.08-1.504-2.31l6.518-11.59zM11 14a1 1 0 10-2 0 1 1 0 002 0zm-1-8a1 1 0 00-.894.553L9 7v4a1 1 0 002 0V7l-.106-.447A1 1 0 0010 6z"
              clip-rule="evenodd"
            />
          </svg>
          Confirmação!
        </h3>
        <p class="text-slate-300 mb-6">Deseja salvar este agente?</p>
        <div class="flex justify-end space-x-3">
          <button
            type="button"
            :disabled="saving"
            class="px-4 py-2 bg-[#2AD7DE] hover:bg-[#5de9f0] text-slate-950 font-semibold rounded-lg transition disabled:opacity-50"
            @click="confirmSave"
          >
            {{ saving ? 'Salvando…' : 'Sim' }}
          </button>
          <button type="button" class="px-4 py-2 border border-slate-600 rounded-lg text-slate-300 hover:bg-slate-800/70 transition" @click="closeSaveConfirm">
            Não
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { useApi } from '../composables/useApi'
import AgentAvatar from './AgentAvatar.vue'
import AgentAvatarPicker from './AgentAvatarPicker.vue'
import AgentSpecialtyDataAnalystForm from './AgentSpecialtyDataAnalystForm.vue'
import AgentSpecialtyRecordCreationForm from './AgentSpecialtyRecordCreationForm.vue'
import AgentSpecialtyKnowledgeForm from './AgentSpecialtyKnowledgeForm.vue'
import AgentSpecialtyEmailAnalystForm from './AgentSpecialtyEmailAnalystForm.vue'
import AgentCoordinatorConfigForm from './AgentCoordinatorConfigForm.vue'
import AgentNetwork from './AgentNetwork.vue'
import {
  DEFAULT_AGENT_AVATAR_VARIANT,
  DEFAULT_AGENT_AVATAR_COLOR,
  normalizeAgentAvatarVariant,
  normalizeAgentAvatarColor
} from '../constants/agentAvatarPresets.js'
import { router, navigateTo } from '../router.js'

export default {
  props: {
    fixedAgentType: {
      type: String,
      default: '',
      validator(v) {
        return ['', 'coordinator', 'executor'].includes(v)
      }
    },
    startInNetworkMode: {
      type: Boolean,
      default: false
    },
    tenantAdmin: {
      type: Boolean,
      required: true
    }
  },

  components: {
    AgentAvatar,
    AgentAvatarPicker,
    AgentSpecialtyDataAnalystForm,
    AgentSpecialtyRecordCreationForm,
    AgentSpecialtyKnowledgeForm,
    AgentSpecialtyEmailAnalystForm,
    AgentCoordinatorConfigForm,
    AgentNetwork
  },

  setup() {
    const { get, post, patch, delete: del, postFormData } = useApi()
    return { get, post, patch, remove: del, postFormData }
  },

  data() {
    return {
      agents: [],
      pagy: null,
      /** 'table' (Modo Card) | 'network' (Modo Rede) */
      listViewMode: 'table',
      agentsNetworkLoading: false,
      loading: true,
      loadError: null,
      searchQ: '',
      filterAgentType: '',
      filterSpecialty: '',
      searchDebounce: null,
      createMenuOpen: false,
      modalOpen: false,
      saveConfirmOpen: false,
      deleteConfirmAgent: null,
      deleting: false,
      editingId: null,
      dataAnalystFormKey: 0,
      form: {
        name: '',
        agent_type: 'executor',
        specialty: 'data_analyst',
        settings: this.defaultAgentSettings ? this.defaultAgentSettings() : {},
        api_connector_endpoint_ids: [],
        database_connector_view_ids: [],
        email_inbox_connector_ids: [],
        allowed_executor_ids: [],
        agent_schedules: [],
        description: '',
        system_prompt: '',
        active: true,
        responds_to_chat: true,
        avatar_variant: DEFAULT_AGENT_AVATAR_VARIANT,
        avatar_color: DEFAULT_AGENT_AVATAR_COLOR
      },
      formErrors: [],
      saving: false,
      editModalLoading: false,
      demoLoadingId: null,
      connectorOptions: {
        apiEndpointOptions: [],
        databaseConnectorViewOptions: [],
        emailInboxConnectorOptions: [],
        whatsappDeliveryConnectorOptions: []
      },
      allowedExecutorSummaries: [],
      /** Remonta os pickers async ao abrir o modal do coordenador. */
      coordinatorFormMountKey: 0,
      selectedApiEndpointToAdd: null,
      selectedDatabaseConnectorViewToAdd: null,
      selectedEmailInboxConnectorToAdd: null,
      knowledgeReindexing: false,
      knowledgeUploading: false,
      knowledgeFiles: [],
      knowledgeDocuments: [],
      knowledgeFileInputKey: 0,
      /** Última execução de reindex (API: last_knowledge_reindex_run); não faz parte de settings. */
      lastKnowledgeReindexRun: null,
      weekDayOptions: [
        { value: 1, label: 'Seg' },
        { value: 2, label: 'Ter' },
        { value: 3, label: 'Qua' },
        { value: 4, label: 'Qui' },
        { value: 5, label: 'Sex' },
        { value: 6, label: 'Sáb' },
        { value: 0, label: 'Dom' }
      ]
    }
  },

  computed: {
    agentTypeFilterLocked() {
      return this.fixedAgentType === 'coordinator' || this.fixedAgentType === 'executor'
    },
    tableHeadingTitle() {
      if (this.fixedAgentType === 'coordinator') return 'Coordenadores'
      if (this.fixedAgentType === 'executor') return 'Executores'
      return 'Agentes'
    },
    tableHeadingSubtitle() {
      if (this.fixedAgentType === 'coordinator') {
        return 'Coordenadores que serão utilizados para responder o chat com os usuários.'
      }
      if (this.fixedAgentType === 'executor') {
        return 'Executores chamados pelos coordenadores conforme especialidade.'
      }
      return 'Configure agentes do seu ambiente (fase 1 — sem LLM real).'
    },
    pageButtons() {
      if (!this.pagy) return []
      const total = this.pagy.pages
      const cur = this.pagy.page
      const out = []
      for (let i = Math.max(1, cur - 2); i <= Math.min(total, cur + 2); i++) out.push(i)
      return out
    },
    isRecordCreationSpecialty() {
      return this.form.agent_type === 'executor' && this.form.specialty === 'record_creation'
    },
    isDataAnalystSpecialty() {
      return this.form.agent_type === 'executor' && this.form.specialty === 'data_analyst'
    },
    isKnowledgeSpecialist() {
      return this.form.agent_type === 'executor' && this.form.specialty === 'knowledge_specialist'
    },
    isEmailAnalystSpecialty() {
      return this.form.agent_type === 'executor' && this.form.specialty === 'email_analyst'
    },
    filteredApiEndpointOptions() {
      const options = this.connectorOptions.apiEndpointOptions || []
      if (this.isRecordCreationSpecialty) {
        return options.filter(
          (opt) =>
            String(opt.http_method || '').toUpperCase() === 'POST' &&
            String(opt.endpoint_intent || '').toLowerCase() === 'write'
        )
      }
      if (this.isDataAnalystSpecialty || this.isKnowledgeSpecialist) {
        return options.filter((opt) => String(opt.endpoint_intent || '').toLowerCase() === 'read')
      }
      return options
    },
    shouldLoadAgentLists() {
      if (this.startInNetworkMode && !this.tenantAdmin) return false
      return true
    }
  },

  watch: {
    'form.specialty'() {
      if (this.form.specialty === 'email_analyst') {
        this.form.api_connector_endpoint_ids = []
        this.form.database_connector_view_ids = []
        this.selectedApiEndpointToAdd = null
        this.selectedDatabaseConnectorViewToAdd = null
      } else {
        this.form.email_inbox_connector_ids = []
        this.selectedEmailInboxConnectorToAdd = null
      }
      if (this.isRecordCreationSpecialty) {
        this.form.database_connector_view_ids = []
        this.selectedDatabaseConnectorViewToAdd = null
      }
      if (this.isKnowledgeSpecialist) {
        this.form.database_connector_view_ids = []
        this.selectedDatabaseConnectorViewToAdd = null
      }
      if (['data_analyst', 'email_analyst'].includes(this.form.specialty)) {
        if (!Array.isArray(this.form.agent_schedules)) this.form.agent_schedules = []
      } else {
        this.form.agent_schedules = []
      }
      this.pruneApiSelectionBySpecialty()
      if (this.isDataAnalystSpecialty) {
        this.ensureDataAnalystDataSourceConsistency()
      }
    }
  },

  mounted() {
    if (this.agentTypeFilterLocked) {
      this.filterAgentType = this.fixedAgentType
      if (this.fixedAgentType === 'coordinator') {
        this.filterSpecialty = ''
      }
    }
    if (this.startInNetworkMode) {
      this.listViewMode = 'network'
    }
    if (this.shouldLoadAgentLists) {
      this.loadAgents()
      this.loadConnectorOptions()
    } else {
      this.loading = false
    }
  },

  methods: {
    onNetworkNodeSelected(id) {
      if (!this.tenantAdmin) return
      this.openEdit({ id })
    },
    dismissAgentFormErrors() {
      this.formErrors = []
    },

    defaultAgentSettings() {
      return {
        knowledge: {
          urls: ['']
        },
        data_analyst: {
          data_source_kind: 'api_endpoint'
        },
        outputs: {
          chat_authorized_emails: ''
        }
      }
    },

    defaultScheduleRowSettings() {
      return {
        schedule: {
          enabled: false,
          mode: 'interval_minutes',
          every_minutes: 60,
          at_time: '08:00',
          weekdays: [1, 2, 3, 4, 5]
        },
        outputs: {
          email: { enabled: false, to: '' },
          whatsapp: { enabled: false, whatsapp_delivery_connector_id: null, to: '' }
        },
        data_analyst: {
          scheduled_system_prompt: '',
          scheduled_fixed_arguments: {},
          scheduled_api_connector_endpoint_id: null,
          scheduled_database_connector_view_id: null
        }
      }
    },

    emptyScheduleRow() {
      return {
        id: null,
        _destroy: false,
        clientKey: `new-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`,
        enabled: false,
        description: '',
        position: 0,
        settings: JSON.parse(JSON.stringify(this.defaultScheduleRowSettings()))
      }
    },

    /** Snippet da descrição para cards (até 120 caracteres); vazio se não houver. */
    formatDescriptionSnippet(a) {
      if (!a) return ''
      const d = String(a.description || '').trim()
      if (!d) return ''
      const max = 120
      if (d.length <= max) return d
      return `${d.slice(0, max)}…`
    },

    agentDescriptionTitle(a) {
      if (!a) return undefined
      const d = String(a.description || '').trim()
      return d || undefined
    },

    formatExecutorSpecialty(a) {
      if (!a) return '—'
      const s = a.specialty
      if (!s) return '—'
      if (s === 'data_analyst') return 'Analista de dados'
      if (s === 'record_creation') return 'Criação de Cadastros'
      if (s === 'knowledge_specialist') return 'Especialista em conhecimento'
      if (s === 'email_analyst') return 'Analista de e-mail'
      return String(s)
    },

    formatCoordinatorCardSecondary(a) {
      return this.formatDescriptionSnippet(a) || '—'
    },

    normalizeAgentSettings(raw) {
      const base = this.defaultAgentSettings()
      if (!raw || typeof raw !== 'object') return base
      const out = JSON.parse(JSON.stringify(base))
      if (raw.knowledge && typeof raw.knowledge === 'object') {
        if (Array.isArray(raw.knowledge.urls)) {
          out.knowledge.urls = raw.knowledge.urls.map((s) => String(s || '')).filter((s) => s.length <= 2048)
          if (out.knowledge.urls.length === 0) out.knowledge.urls = ['']
        }
      }
      if (raw.data_analyst && typeof raw.data_analyst === 'object') {
        const k = String(raw.data_analyst.data_source_kind || '').trim()
        out.data_analyst.data_source_kind = k === 'database_view' ? 'database_view' : 'api_endpoint'
      }
      if (raw.outputs && typeof raw.outputs === 'object' && raw.outputs.chat_authorized_emails !== undefined) {
        out.outputs.chat_authorized_emails = String(raw.outputs.chat_authorized_emails || '')
      }
      return out
    },

    normalizeScheduleRowSingleId(raw) {
      const n = Number(raw)
      return Number.isFinite(n) && n > 0 ? n : null
    },

    normalizeScheduleRowSettings(raw) {
      const base = this.defaultScheduleRowSettings()
      if (!raw || typeof raw !== 'object') return base
      const out = JSON.parse(JSON.stringify(base))
      if (raw.schedule && typeof raw.schedule === 'object') {
        if (raw.schedule.enabled !== undefined) out.schedule.enabled = !!raw.schedule.enabled
        if (['interval_minutes', 'daily_at', 'weekly_at'].includes(raw.schedule.mode)) {
          out.schedule.mode = raw.schedule.mode
        }
        const n = Number(raw.schedule.every_minutes)
        if (!Number.isNaN(n) && n > 0) out.schedule.every_minutes = n
        const t = String(raw.schedule.at_time || '').trim()
        if (/^\d{2}:\d{2}$/.test(t)) out.schedule.at_time = t
        if (Array.isArray(raw.schedule.weekdays)) {
          out.schedule.weekdays = raw.schedule.weekdays
            .map((v) => Number(v))
            .filter((v) => Number.isInteger(v) && v >= 0 && v <= 6)
        }
      }
      if (raw.outputs && typeof raw.outputs === 'object' && raw.outputs.email && typeof raw.outputs.email === 'object') {
        if (raw.outputs.email.enabled !== undefined) out.outputs.email.enabled = !!raw.outputs.email.enabled
        if (raw.outputs.email.to !== undefined) out.outputs.email.to = String(raw.outputs.email.to || '')
      }
      if (raw.outputs && typeof raw.outputs === 'object' && raw.outputs.whatsapp && typeof raw.outputs.whatsapp === 'object') {
        const w = raw.outputs.whatsapp
        if (w.enabled !== undefined) out.outputs.whatsapp.enabled = !!w.enabled
        if (w.whatsapp_delivery_connector_id !== undefined && w.whatsapp_delivery_connector_id !== null && w.whatsapp_delivery_connector_id !== '') {
          const n = Number(w.whatsapp_delivery_connector_id)
          out.outputs.whatsapp.whatsapp_delivery_connector_id = Number.isFinite(n) ? n : null
        } else if (w.whatsapp_delivery_connector_id === null || w.whatsapp_delivery_connector_id === '') {
          out.outputs.whatsapp.whatsapp_delivery_connector_id = null
        }
        if (w.to !== undefined) out.outputs.whatsapp.to = String(w.to || '')
      }
      const SCHEDULED_PROMPT_MAX = 50000
      if (raw.data_analyst && typeof raw.data_analyst === 'object') {
        let sp = String(raw.data_analyst.scheduled_system_prompt || '')
        if (sp.length > SCHEDULED_PROMPT_MAX) sp = sp.slice(0, SCHEDULED_PROMPT_MAX)
        out.data_analyst.scheduled_system_prompt = sp
        const sfa = raw.data_analyst.scheduled_fixed_arguments
        if (sfa !== undefined && sfa !== null && typeof sfa === 'object' && !Array.isArray(sfa)) {
          try {
            out.data_analyst.scheduled_fixed_arguments = JSON.parse(JSON.stringify(sfa))
          } catch {
            out.data_analyst.scheduled_fixed_arguments = {}
          }
        } else {
          out.data_analyst.scheduled_fixed_arguments = {}
        }
        delete out.data_analyst.scheduled_api_connector_endpoint_ids
        delete out.data_analyst.scheduled_database_connector_view_ids
        out.data_analyst.scheduled_api_connector_endpoint_id = this.normalizeScheduleRowSingleId(
          raw.data_analyst.scheduled_api_connector_endpoint_id
        )
        out.data_analyst.scheduled_database_connector_view_id = this.normalizeScheduleRowSingleId(
          raw.data_analyst.scheduled_database_connector_view_id
        )
      }
      if (out.outputs && typeof out.outputs === 'object') {
        delete out.outputs.chat_authorized_emails
      }
      return out
    },

    normalizeAgentSchedulesFromApi(list) {
      if (!Array.isArray(list) || list.length === 0) return []
      return list.map((row, idx) => ({
        id: row.id,
        clientKey: row.id != null ? `srv-${row.id}` : `tmp-${idx}`,
        _destroy: false,
        enabled: !!row.enabled,
        description: String(row.description || ''),
        position: row.position ?? idx,
        settings: this.normalizeScheduleRowSettings(row.settings || {})
      }))
    },

    normalizeEmailRecipients(raw) {
      return String(raw || '')
        .split(/[;\n,]+/)
        .map((s) => s.trim())
        .filter(Boolean)
        .join('; ')
    },

    onAvatarVariant(v) {
      this.form.avatar_variant = v
    },

    onAvatarColor(c) {
      this.form.avatar_color = c
    },

    buildAgentSchedulesAttributesForPayload() {
      const rows = Array.isArray(this.form.agent_schedules) ? this.form.agent_schedules : []
      return rows.map((row, idx) => {
        const st = this.normalizeScheduleRowSettings(row.settings)
        st.outputs.email.to = this.normalizeEmailRecipients(st.outputs.email.to)
        const base = {
          enabled: !!row.enabled,
          description: String(row.description || ''),
          position: idx,
          settings: st
        }
        if (row.id != null && row.id !== '') base.id = row.id
        if (row._destroy) base._destroy = true
        return [String(idx), base]
      })
    },

    agentPayload() {
      const settings = this.normalizeAgentSettings(this.form.settings)
      if (this.isDataAnalystSpecialty || this.isEmailAnalystSpecialty) {
        if (!settings.outputs) settings.outputs = {}
        settings.outputs.chat_authorized_emails = this.normalizeEmailRecipients(
          settings.outputs.chat_authorized_emails || ''
        )
      }

      let apiConnectorEndpointIds =
        this.form.agent_type === 'executor' && this.form.specialty !== 'email_analyst'
          ? this.form.api_connector_endpoint_ids
          : []
      let databaseConnectorViewIds =
        this.form.agent_type === 'executor' &&
        !['record_creation', 'knowledge_specialist', 'email_analyst'].includes(this.form.specialty)
          ? this.form.database_connector_view_ids
          : []

      if (this.isDataAnalystSpecialty) {
        const kind = settings.data_analyst?.data_source_kind || 'api_endpoint'
        if (kind === 'api_endpoint') {
          databaseConnectorViewIds = []
        } else {
          apiConnectorEndpointIds = []
        }
      }

      const payload = {
        name: this.form.name,
        agent_type: this.form.agent_type,
        specialty: this.form.agent_type === 'executor' ? this.form.specialty : null,
        settings,
        api_connector_endpoint_ids: apiConnectorEndpointIds,
        database_connector_view_ids: databaseConnectorViewIds,
        email_inbox_connector_ids:
          this.form.agent_type === 'executor' && this.form.specialty === 'email_analyst'
            ? (this.form.email_inbox_connector_ids || []).map((x) => Number(x)).filter((n) => Number.isFinite(n))
            : [],
        allowed_executor_ids: this.form.agent_type === 'coordinator' ? this.form.allowed_executor_ids : [],
        description: this.form.description,
        system_prompt: this.form.system_prompt,
        active: this.form.active,
        responds_to_chat:
          this.form.agent_type === 'coordinator' ? !!this.form.responds_to_chat : true,
        avatar_variant: normalizeAgentAvatarVariant(this.form.avatar_variant),
        avatar_color: normalizeAgentAvatarColor(this.form.avatar_color)
      }
      if (this.isDataAnalystSpecialty || this.isEmailAnalystSpecialty) {
        payload.agent_schedules_attributes = Object.fromEntries(this.buildAgentSchedulesAttributesForPayload())
      }
      return payload
    },

    toggleCreateMenu() {
      this.createMenuOpen = !this.createMenuOpen
    },

    openAgentsNetworkView() {
      this.agentsNetworkLoading = false
      this.listViewMode = 'network'
    },

    closeAgentsNetworkView() {
      if (router.currentRoute === 'agents-network') {
        navigateTo('agents-coordinators')
      } else {
        this.listViewMode = 'table'
      }
    },

    refreshAgentsNetwork() {
      this.$refs.agentsNetworkEmbed?.loadNetwork()
    },

    addSelectedApiEndpoint() {
      const id = this.selectedApiEndpointToAdd
      if (this.isRecordCreationSpecialty) {
        if (id != null && id !== '' && Number.isFinite(Number(id))) {
          this.form.api_connector_endpoint_ids = [Number(id)]
        } else {
          this.form.api_connector_endpoint_ids = []
        }
        this.selectedApiEndpointToAdd = null
        return
      }
      if (!id) return
      if (!this.form.api_connector_endpoint_ids.includes(id)) {
        this.form.api_connector_endpoint_ids.push(id)
      }
      this.selectedApiEndpointToAdd = null
    },

    pruneApiSelectionBySpecialty() {
      if (
        !this.isRecordCreationSpecialty &&
        !this.isDataAnalystSpecialty &&
        !this.isKnowledgeSpecialist &&
        !this.isEmailAnalystSpecialty
      ) {
        return
      }
      if (this.isEmailAnalystSpecialty) {
        this.form.api_connector_endpoint_ids = []
        return
      }
      const allowedIds = new Set(this.filteredApiEndpointOptions.map((opt) => opt.id))
      this.form.api_connector_endpoint_ids = this.form.api_connector_endpoint_ids.filter((id) => allowedIds.has(id))
      if (this.isRecordCreationSpecialty && this.form.api_connector_endpoint_ids.length > 1) {
        this.form.api_connector_endpoint_ids = this.form.api_connector_endpoint_ids.slice(0, 1)
      }
      this.pruneDatabaseViewSelectionForDataAnalyst()
    },

    pruneDatabaseViewSelectionForDataAnalyst() {
      if (!this.isDataAnalystSpecialty) return
      const allowed = new Set((this.connectorOptions.databaseConnectorViewOptions || []).map((o) => o.id))
      this.form.database_connector_view_ids = (this.form.database_connector_view_ids || []).filter((id) =>
        allowed.has(id)
      )
    },

    removeApiEndpoint(id) {
      this.form.api_connector_endpoint_ids = this.form.api_connector_endpoint_ids.filter((x) => x !== id)
    },

    addSelectedDatabaseConnectorView() {
      const id = this.selectedDatabaseConnectorViewToAdd
      if (!id) return
      if (!this.form.database_connector_view_ids.includes(id)) {
        this.form.database_connector_view_ids.push(id)
      }
      this.selectedDatabaseConnectorViewToAdd = null
    },

    removeDatabaseConnectorView(id) {
      this.form.database_connector_view_ids = this.form.database_connector_view_ids.filter((x) => x !== id)
    },

    addSelectedEmailInboxConnector() {
      const id = this.selectedEmailInboxConnectorToAdd
      if (id == null || id === '') return
      const n = Number(id)
      if (!Number.isFinite(n)) return
      const ids = Array.isArray(this.form.email_inbox_connector_ids) ? this.form.email_inbox_connector_ids : []
      if (!ids.some((x) => Number(x) === n)) ids.push(n)
      this.form.email_inbox_connector_ids = ids
      this.selectedEmailInboxConnectorToAdd = null
    },

    removeEmailInboxConnector(id) {
      const n = Number(id)
      this.form.email_inbox_connector_ids = (this.form.email_inbox_connector_ids || []).filter((x) => Number(x) !== n)
    },

    addSelectedExecutor(row) {
      if (!row || row.id == null) return
      const id = Number(row.id)
      if (!Number.isFinite(id) || Number(id) === Number(this.editingId)) return
      const ids = this.form.allowed_executor_ids.map((x) => Number(x))
      if (ids.includes(id)) return
      this.form.allowed_executor_ids.push(id)
      const label = row.label || row.name || `Executor #${id}`
      if (!this.allowedExecutorSummaries.some((s) => Number(s.id) === id)) {
        this.allowedExecutorSummaries.push({
          id,
          name: row.name || label,
          specialty: row.specialty,
          label
        })
      }
    },

    removeExecutor(id) {
      const n = Number(id)
      this.form.allowed_executor_ids = this.form.allowed_executor_ids.filter((x) => Number(x) !== n)
      this.allowedExecutorSummaries = this.allowedExecutorSummaries.filter((s) => Number(s.id) !== n)
    },

    async loadConnectorOptions() {
      const { data, error } = await this.get('/api/v1/agents/connector_options')
      if (error || !data) return
      this.connectorOptions = {
        apiEndpointOptions: data.api_endpoint_options || data.apiEndpointOptions || [],
        databaseConnectorViewOptions:
          data.database_connector_view_options || data.databaseConnectorViewOptions || [],
        emailInboxConnectorOptions: data.email_inbox_connector_options || [],
        whatsappDeliveryConnectorOptions: data.whatsapp_delivery_connector_options || []
      }
      this.pruneApiSelectionBySpecialty()
      this.pruneDatabaseViewSelectionForDataAnalyst()
    },

    debouncedLoad() {
      clearTimeout(this.searchDebounce)
      this.searchDebounce = setTimeout(() => this.loadAgents(1), 350)
    },

    refreshAgentsList() {
      this.loadAgents(this.pagy?.page || 1)
    },

    async loadAgents(page = 1) {
      this.loading = true
      this.loadError = null
      const q = this.searchQ.trim()
      const qs = new URLSearchParams({ page: String(page) })
      if (q) qs.set('q', q)
      if (this.filterAgentType) qs.set('agent_type', this.filterAgentType)
      if (this.filterSpecialty) qs.set('specialty', this.filterSpecialty)
      const { data, error } = await this.get(`/api/v1/agents?${qs}`)
      this.loading = false
      if (error) {
        this.loadError = 'Não foi possível carregar os agentes.'
        return
      }
      this.agents = data.data || []
      this.pagy = data.pagy || null
    },

    goPage(p) {
      this.loadAgents(p)
    },

    closeAgentModal() {
      this.modalOpen = false
      this.saveConfirmOpen = false
      this.editModalLoading = false
      this.lastKnowledgeReindexRun = null
      this.allowedExecutorSummaries = []
    },

    openSaveConfirm() {
      if (this.editModalLoading) return
      this.saveConfirmOpen = true
    },

    closeSaveConfirm() {
      this.saveConfirmOpen = false
    },

    confirmSave() {
      this.saveConfirmOpen = false
      this.save()
    },

    confirmDestroy(a) {
      this.deleteConfirmAgent = a
    },

    closeDeleteModal() {
      this.deleteConfirmAgent = null
      this.deleting = false
    },

    async executeDestroy() {
      if (!this.deleteConfirmAgent) return
      this.deleting = true
      const id = this.deleteConfirmAgent.id
      const { error } = await this.remove(`/api/v1/agents/${id}`)
      this.deleting = false
      if (error) {
        this.loadError = 'Não foi possível excluir.'
        return
      }
      this.closeDeleteModal()
      await this.applyAgentDeletedLocally(id)
    },

    /**
     * Remove o agente da lista sem refetch. Só volta a pedir a API se a página atual ficar vazia
     * mas ainda existirem agentes noutras páginas (carrega a página válida mais próxima).
     */
    async applyAgentDeletedLocally(deletedId) {
      this.agents = this.agents.filter((a) => a.id !== deletedId)
      if (!this.pagy) return

      const items = this.pagy.items || 20
      const page = this.pagy.page || 1
      const newCount = Math.max(0, (this.pagy.count || 0) - 1)
      const newPages = newCount === 0 ? 1 : Math.max(1, Math.ceil(newCount / items))

      if (this.agents.length === 0) {
        if (newCount === 0) {
          this.pagy = {
            ...this.pagy,
            count: 0,
            pages: 1,
            last: 1,
            from: 0,
            to: 0,
            prev: null,
            next: null
          }
          return
        }
        await this.loadAgents(Math.min(page, newPages))
        return
      }

      const from = (page - 1) * items + 1
      const to = Math.min(page * items, newCount)
      this.pagy = {
        ...this.pagy,
        count: newCount,
        pages: newPages,
        last: newPages,
        from,
        to,
        prev: page > 1 ? page - 1 : null,
        next: page < newPages ? page + 1 : null
      }
    },

    openCreate(agentType = 'executor') {
      this.createMenuOpen = false
      this.saveConfirmOpen = false
      this.editingId = null
      if (agentType === 'executor') this.dataAnalystFormKey += 1
      const isCoordinator = agentType === 'coordinator'
      this.form = {
        name: '',
        agent_type: isCoordinator ? 'coordinator' : 'executor',
        specialty: isCoordinator ? null : 'data_analyst',
        settings: this.defaultAgentSettings(),
        api_connector_endpoint_ids: isCoordinator ? [] : [],
        database_connector_view_ids: isCoordinator ? [] : [],
        email_inbox_connector_ids: isCoordinator ? [] : [],
        allowed_executor_ids: isCoordinator ? [] : [],
        agent_schedules: [],
        description: '',
        system_prompt: '',
        active: true,
        responds_to_chat: true,
        avatar_variant: isCoordinator ? 'coord_hub' : DEFAULT_AGENT_AVATAR_VARIANT,
        avatar_color: DEFAULT_AGENT_AVATAR_COLOR
      }
      this.selectedApiEndpointToAdd = null
      this.selectedDatabaseConnectorViewToAdd = null
      this.selectedEmailInboxConnectorToAdd = null
      this.formErrors = []
      this.knowledgeFiles = []
      this.knowledgeDocuments = []
      this.knowledgeFileInputKey = 0
      this.lastKnowledgeReindexRun = null
      this.allowedExecutorSummaries = []
      if (isCoordinator) this.coordinatorFormMountKey += 1
      this.editModalLoading = false
      this.modalOpen = true
    },

    applyAgentFromShow(a) {
      const spec = (a.agent_type || 'executor') === 'executor' ? (a.specialty || 'data_analyst') : null
      const agentSchedules = this.normalizeAgentSchedulesFromApi(a.agent_schedules || [])
      this.form = {
        name: a.name,
        agent_type: a.agent_type || 'executor',
        specialty: spec,
        settings: this.normalizeAgentSettings(a.settings),
        api_connector_endpoint_ids: Array.isArray(a.api_connector_endpoint_ids) ? a.api_connector_endpoint_ids : [],
        database_connector_view_ids: Array.isArray(a.database_connector_view_ids) ? a.database_connector_view_ids : [],
        email_inbox_connector_ids: Array.isArray(a.email_inbox_connector_ids) ? a.email_inbox_connector_ids : [],
        allowed_executor_ids: Array.isArray(a.allowed_executor_ids) ? a.allowed_executor_ids : [],
        agent_schedules: agentSchedules,
        description: a.description || '',
        system_prompt: a.system_prompt || '',
        active: !!a.active,
        responds_to_chat:
          (a.agent_type || 'executor') === 'coordinator' ? a.responds_to_chat !== false : true,
        avatar_variant: normalizeAgentAvatarVariant(a.avatar_variant),
        avatar_color: normalizeAgentAvatarColor(a.avatar_color)
      }
      if (this.form.agent_type === 'executor' && this.form.specialty === 'data_analyst') {
        this.ensureDataAnalystDataSourceConsistency()
      }
      this.knowledgeDocuments = Array.isArray(a.knowledge_documents) ? a.knowledge_documents : []
      this.lastKnowledgeReindexRun = a.last_knowledge_reindex_run || null
      this.allowedExecutorSummaries = Array.isArray(a.allowed_executor_summaries)
        ? a.allowed_executor_summaries.map((s) => ({ ...s }))
        : []
    },

    ensureDataAnalystDataSourceConsistency() {
      const da = this.form.settings?.data_analyst
      if (!da) return
      let kind = da.data_source_kind
      if (kind !== 'database_view' && kind !== 'api_endpoint') {
        const hasApi = (this.form.api_connector_endpoint_ids || []).length > 0
        const hasDb = (this.form.database_connector_view_ids || []).length > 0
        da.data_source_kind = hasDb && !hasApi ? 'database_view' : 'api_endpoint'
        kind = da.data_source_kind
      }
    },

    async openEdit(a) {
      const id = a?.id
      if (!id) return

      this.createMenuOpen = false
      this.saveConfirmOpen = false
      this.editingId = id
      this.selectedApiEndpointToAdd = null
      this.selectedDatabaseConnectorViewToAdd = null
      this.selectedEmailInboxConnectorToAdd = null
      this.formErrors = []
      this.knowledgeFiles = []
      this.knowledgeDocuments = []
      this.knowledgeFileInputKey = 0
      this.lastKnowledgeReindexRun = null
      this.allowedExecutorSummaries = []
      this.coordinatorFormMountKey += 1
      this.form = {
        name: '',
        agent_type: 'executor',
        specialty: 'data_analyst',
        settings: this.defaultAgentSettings(),
        api_connector_endpoint_ids: [],
        database_connector_view_ids: [],
        email_inbox_connector_ids: [],
        allowed_executor_ids: [],
        agent_schedules: [],
        description: '',
        system_prompt: '',
        active: true,
        responds_to_chat: true,
        avatar_variant: DEFAULT_AGENT_AVATAR_VARIANT,
        avatar_color: DEFAULT_AGENT_AVATAR_COLOR
      }
      this.editModalLoading = true
      this.modalOpen = true

      const [{ data, error }] = await Promise.all([
        this.get(`/api/v1/agents/${id}`),
        this.loadConnectorOptions()
      ])
      this.editModalLoading = false

      if (!this.modalOpen || this.editingId !== id) return

      if (error || !data) {
        const msg = error?.response?.data?.errors
        this.formErrors = Array.isArray(msg)
          ? msg
          : [error?.response?.data?.error || 'Não foi possível carregar o agente. Faça login novamente ou tente outra vez.']
        return
      }

      this.dataAnalystFormKey += 1
      this.applyAgentFromShow(data)
    },

    onKnowledgeFilesSelected(e) {
      const files = Array.from(e?.target?.files || [])
      this.knowledgeFiles = files
    },

    async removeKnowledgeDocument(signedId) {
      if (!this.editingId || !signedId) return
      this.knowledgeUploading = true
      this.formErrors = []
      try {
        const qs = new URLSearchParams({ signed_id: String(signedId) })
        const { data, error } = await this.remove(`/api/v1/agents/${this.editingId}/knowledge_documents?${qs}`)
        if (error) {
          const msg = error.response?.data?.errors
          this.formErrors = Array.isArray(msg) ? msg : [error.response?.data?.error || 'Não foi possível remover o arquivo']
          return
        }
        if (data?.errors) {
          this.formErrors = data.errors
          return
        }
        this.knowledgeDocuments = Array.isArray(data?.knowledge_documents) ? data.knowledge_documents : []
        if (data?.last_knowledge_reindex_run !== undefined) {
          this.lastKnowledgeReindexRun = data.last_knowledge_reindex_run
        }
      } finally {
        this.knowledgeUploading = false
      }
    },

    async uploadKnowledgePending(agentId) {
      if (!this.isKnowledgeSpecialist || !this.knowledgeFiles?.length) return { ok: true }
      const fd = new FormData()
      this.knowledgeFiles.forEach((f) => fd.append('knowledge_documents[]', f))
      const { data, error } = await this.postFormData(
        `/api/v1/agents/${agentId}/knowledge_documents`,
        fd
      )
      if (error) {
        const msg = error.response?.data?.errors
        return {
          ok: false,
          errors: Array.isArray(msg) ? msg : [error.response?.data?.error || 'Falha no envio dos documentos']
        }
      }
      if (data?.errors) return { ok: false, errors: data.errors }
      this.knowledgeDocuments = Array.isArray(data?.knowledge_documents) ? data.knowledge_documents : []
      if (data?.last_knowledge_reindex_run !== undefined) {
        this.lastKnowledgeReindexRun = data.last_knowledge_reindex_run
      }
      this.knowledgeFiles = []
      this.knowledgeFileInputKey += 1
      return { ok: true }
    },

    addKnowledgeUrlRow() {
      if (!this.form.settings?.knowledge?.urls) this.form.settings.knowledge.urls = ['']
      this.form.settings.knowledge.urls.push('')
    },

    removeKnowledgeUrlRow(idx) {
      const arr = this.form.settings?.knowledge?.urls || []
      this.form.settings.knowledge.urls = arr.filter((_, i) => i !== idx)
      if (this.form.settings.knowledge.urls.length === 0) this.form.settings.knowledge.urls = ['']
    },

    updateKnowledgeUrl({ index, value }) {
      if (!this.form.settings?.knowledge?.urls) this.form.settings.knowledge.urls = ['']
      this.form.settings.knowledge.urls[index] = String(value || '')
    },

    async reindexKnowledge() {
      if (!this.editingId) return
      this.knowledgeReindexing = true
      this.formErrors = []
      try {
        const { data, error } = await this.post(`/api/v1/agents/${this.editingId}/knowledge_reindex`, {})
        if (error) {
          const msg = error.response?.data?.errors
          this.formErrors = Array.isArray(msg)
            ? msg
            : [error.response?.data?.error || 'Falha ao solicitar reindexação.']
          return
        }
        if (data?.settings) {
          this.form.settings = this.normalizeAgentSettings(data.settings)
        }
        if (data?.last_knowledge_reindex_run !== undefined) {
          this.lastKnowledgeReindexRun = data.last_knowledge_reindex_run
        }
      } finally {
        this.knowledgeReindexing = false
      }
    },

    async save() {
      this.formErrors = []
      if (this.isDataAnalystSpecialty) {
        const msg = this.$refs.dataAnalystSpecialtyForm?.validateScheduledFixedJson?.()
        if (msg) {
          this.formErrors = [msg]
          return
        }
      }
      this.saving = true
      try {
        let agentId = this.editingId
        if (this.editingId) {
          const { data, error } = await this.patch(`/api/v1/agents/${this.editingId}`, {
            agent: this.agentPayload()
          })
          if (error) {
            const msg = error.response?.data?.errors
            this.formErrors = Array.isArray(msg) ? msg : [error.response?.data?.error || 'Erro ao salvar']
            return
          }
          if (data?.errors) {
            this.formErrors = data.errors
            return
          }
        } else {
          const { data, error } = await this.post('/api/v1/agents', { agent: this.agentPayload() })
          if (error) {
            const msg = error.response?.data?.errors
            this.formErrors = Array.isArray(msg) ? msg : [error.response?.data?.error || 'Erro ao salvar']
            return
          }
          if (data?.errors) {
            this.formErrors = data.errors
            return
          }
          agentId = data?.id ?? null
        }

        if (agentId && this.isKnowledgeSpecialist && this.knowledgeFiles.length) {
          const up = await this.uploadKnowledgePending(agentId)
          if (!up.ok) {
            this.formErrors = up.errors || ['Falha no envio dos documentos']
            return
          }
        }

        this.closeAgentModal()
        await this.loadAgents(this.pagy?.page || 1)
        if (this.listViewMode === 'network') {
          await this.$nextTick()
          this.$refs.agentsNetworkEmbed?.loadNetwork()
        }
      } catch (err) {
        const msg = err.response?.data?.errors
        this.formErrors = Array.isArray(msg) ? msg : [err.response?.data?.error || 'Erro ao salvar']
      } finally {
        this.saving = false
      }
    },

    async demoRun(a) {
      this.demoLoadingId = a.id
      try {
        const { data, error } = await this.post(`/api/v1/agents/${a.id}/demo_run`, {})
        if (error) throw error
        window.alert(
          `Demo enfileirada. Run #${data.data?.id} — estado: ${data.data?.status}. ` +
            'Acompanhe no painel Execuções (atualiza automaticamente).'
        )
      } catch (e) {
        window.alert('Falha ao rodar demo.')
      } finally {
        this.demoLoadingId = null
      }
    }
  }
}
</script>

<style scoped>
.time-input::-webkit-calendar-picker-indicator {
  filter: invert(80%) sepia(8%) saturate(230%) hue-rotate(184deg) brightness(103%) contrast(94%);
  opacity: 1;
}
</style>
